import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminOperatorAccessDetail,
  updateYowanAdminOperatorProfile,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const operatorProfileDefaults = {
  name: '',
  nameKana: '',
  email: '',
  phone: '',
  postalCode: '',
  address: '',
  note: '',
};

const buildOperatorProfileFormValues = (record = {}) => ({
  name: record.name || '',
  nameKana: record.nameKana || '',
  email: record.email || record.accountEmail || '',
  phone: record.phone || '',
  postalCode: record.postalCode || '',
  address: record.address || '',
  note: record.note || '',
});

const AdminOperatorsEditPage = () => {
  const content = yowanPageContent['/admin/operators/:accessId/edit'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const { accessId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord ? buildOperatorProfileFormValues(currentRecord) : operatorProfileDefaults,
  });

  useEffect(() => {
    if (!content || !accessId) {
      return undefined;
    }

    let isMounted = true;

    const loadOperator = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const record = await fetchYowanAdminOperatorAccessDetail(accessId);

        if (!isMounted) {
          return;
        }

        setCurrentRecord(record);
        reset(buildOperatorProfileFormValues(record));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setCurrentRecord(null);
        setLoadError(content.form.loadErrorMessage);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOperator();

    return () => {
      isMounted = false;
    };
  }, [accessId, content, reset]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      await updateYowanAdminOperatorProfile(accessId, values);
      setSubmitMessage(content.form.successMessage);
      navigate(appendYowanPreviewQueryIfNeeded('/admin/operators', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(content.form.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/operators" routeKey="/admin/operators/:accessId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/operators', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      {isLoading || loadError || !currentRecord ? (
        <div className="yowan-admin-form c-surface-content">
          {isLoading ? <LoadingSpinner size="sm" /> : loadError || content.form.loadErrorMessage}
        </div>
      ) : (
        <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
          {currentRecord.profileMissing ? (
            <p className="yowan-admin-form__message">{content.form.profileMissingMessage}</p>
          ) : null}

          <label className="c-form__field">
            <span className="c-form__label">
              {content.form.labels.name}
              <em className="c-form__required">*</em>
            </span>
            <input
              className="c-form__control"
              type="text"
              {...register('name', { required: content.form.errors.required })}
            />
            {errors?.name ? <em className="yowan-admin-form__error">{errors.name.message}</em> : null}
          </label>

          <label className="c-form__field">
            <span className="c-form__label">{content.form.labels.nameKana}</span>
            <input className="c-form__control" type="text" {...register('nameKana')} />
          </label>

          <label className="c-form__field">
            <span className="c-form__label">
              {content.form.labels.email}
              <em className="c-form__required">*</em>
            </span>
            <input
              className="c-form__control"
              type="email"
              {...register('email', { required: content.form.errors.required })}
            />
            {errors?.email ? <em className="yowan-admin-form__error">{errors.email.message}</em> : null}
            {content.form.emailHelpText ? (
              <p
                className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
              >
                {content.form.emailHelpText}
              </p>
            ) : null}
          </label>

          <label className="c-form__field">
            <span className="c-form__label">{content.form.labels.phone}</span>
            <input className="c-form__control" type="text" {...register('phone')} />
          </label>

          <label className="c-form__field">
            <span className="c-form__label">{content.form.labels.postalCode}</span>
            <input className="c-form__control" type="text" {...register('postalCode')} />
          </label>

          <label className="c-form__field">
            <span className="c-form__label">{content.form.labels.address}</span>
            <textarea className="c-form__control c-form__control--textarea" rows="3" {...register('address')} />
          </label>

          <label className="c-form__field">
            <span className="c-form__label">{content.form.labels.note}</span>
            <textarea className="c-form__control c-form__control--textarea" rows="4" {...register('note')} />
          </label>

          <div className="yowan-admin-form__actions">
            <button type="submit" className="c-form__submit" disabled={isSubmitting}>
              {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
            </button>
          </div>

          {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
        </form>
      )}
    </AdminBlogShell>
  );
};

export default AdminOperatorsEditPage;
